import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { ViewContractsContractTermsCard } from 'customer-management/customer-details/view-contracts/view-contracts-contract-terms-card';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { CONTRACT_TERMS } from 'shared/constants';
import { Term, TermForm, TERM_BUCKET, TERM_TYPE } from 'shared/types';
import { ViewContractsDiffingTermsCard } from './view-contracts-diffing-terms-card';

interface Props {
  isEditMode: boolean;
  isDiffingMode: boolean;
  isDraft: boolean;
  isRejected: boolean;
  comparingContractTerms: Term[] | undefined;
}
export interface combinedTerm {
  name: string;
  currentValue?: string | number;
  againstValue?: string | number;
  display: string;
  bucket: TERM_BUCKET;
  type: TERM_TYPE;
}

export function ViewContractsContractTermsContainer({
  isEditMode,
  isDiffingMode,
  isDraft,
  isRejected,
  comparingContractTerms,
}: Props) {
  const watchedFields = useWatch({ name: 'terms' });
  const { fields, append, remove } = useFieldArray<{ terms: TermForm[] }>({
    name: 'terms',
  });

  const controlledFields = useMemo(
    () =>
      fields.map((field, index) => ({
        ...field,
        ...watchedFields[index],
      })),
    [fields, watchedFields]
  );

  const combinedContractFields = useMemo(() => {
    const combinedContractFieldsArray: combinedTerm[] = [];
    CONTRACT_TERMS.forEach(term => {
      const finalTerm: combinedTerm = { name: term.name, display: term.display, bucket: term.bucket, type: term.type };
      controlledFields.forEach(field => {
        if (term.name === field.name) {
          finalTerm.currentValue = field.amount;
        }
      });
      comparingContractTerms?.forEach(field => {
        if (term.name === field.name) {
          finalTerm.againstValue = field.amount;
        }
      });
      if (finalTerm.currentValue || finalTerm?.againstValue) {
        combinedContractFieldsArray.push(finalTerm);
      }
    });
    return combinedContractFieldsArray;
  }, [controlledFields, comparingContractTerms]);

  const addTerm = useCallback(() => {
    append({
      type: undefined,
      bucket: undefined,
      display: undefined,
      amount: undefined,
      isNew: true,
    });
  }, [append]);

  const removeTerm = useCallback(
    (index: number) => () => {
      remove(index);
    },
    [remove]
  );
  const availableOptions = useMemo(
    () =>
      CONTRACT_TERMS.filter(term => !fields.some(t => t.name === term.name)).sort((a, b) => {
        if (a.display < b.display) {
          return -1;
        }
        if (a.display > b.display) {
          return 1;
        }
        return 0;
      }),
    [fields]
  );

  const rejectedAndEditMode = isEditMode && isRejected;

  return (
    <>
      {isEditMode && !isDiffingMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pb: 2, pt: 1 }}>
          <Button onClick={addTerm} variant='outlined' color='primary' startIcon={<Add />}>
            Add Term
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 2,
          paddingBottom: 2,
        }}
      >
        {(rejectedAndEditMode || (!isDiffingMode && !isDraft)) &&
          controlledFields.map((field, index) => (
            <ViewContractsContractTermsCard
              key={field.id}
              field={field}
              index={index}
              isRejected={isRejected}
              remove={removeTerm(index)}
              availableOptions={availableOptions}
            />
          ))}
        {(isDiffingMode || isDraft) &&
          !rejectedAndEditMode &&
          combinedContractFields.map((field, index) => (
            <ViewContractsDiffingTermsCard key={field?.name} field={field} index={index} isRejected={isRejected} />
          ))}
      </Box>
    </>
  );
}
